import './styles.scss';
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Container, Icon } from '@edx/paragon';
import {
  Calendar, EventBusy, MoneyOutline, Person,
} from '@edx/paragon/icons';
import { connect } from 'react-redux';
import { defineMessages, injectIntl, FormattedMessage } from 'gatsby-plugin-react-intl';

import { PRODUCT_MAP_2U } from '@src/utils/discoveryConstants';
import { intlShape } from '@prospectus/common/types/intlShape';
import { formatNumberRange, getI18nDateString } from '@utils/formatting';
import { WebviewContext } from '@utils/context';
import { defaultUserCurrency } from '@utils/i18n';
import {
  FREE, UPGRADABLE, PAID, isArchived,
} from '@utils/course';
import { currencyInfoShape } from '@prospectus/common/types/allCurrencyShape';
// TODO: [WS-4589] remove monetization experiment code
// import useMonetizationAuditTrackUpdateExperiment
// from '@prospectus/open-course-page/hooks/useMonetizationAuditTrackUpdateExperiment';

import FreeLanguage from './components/FreeLanguage';
import PaidLanguage from './components/PaidLanguage';
import UpgradableLanguage from './components/UpgradableLanguage';
import WeeksToComplete from './components/WeeksToComplete';

const messages = defineMessages({
  'prospectus.course.weekly.effort_range': {
    id: 'prospectus.weekly.course.effort_range',
    description: 'Estimated range of effort (in hours per week) to complete this course',
    defaultMessage: '{effortRange} hours per week',
  },
  'prospectus.course.snapshot.not-available': {
    id: 'prospectus.course.snapshot.not-available',
    description: "Describes that the course isn't available for enrollment",
    defaultMessage: 'Not currently available',
  },
  'prospectus.course.snapshot.archived': {
    id: 'prospectus.course.snapshot.archived',
    description: 'Describes that the course is archived',
    defaultMessage: 'This course is archived',
  },
});

function BaseCourseSnapshot({
  courseType,
  coursePrice,
  weeksToComplete,
  paymentOption,
  title,
  activeCourseRun,
  activeCourseRun: {
    minEffort,
    maxEffort,
    pacingType,
    start,
    end,
    enrollmentEnd,
  },
  userCurrency,
  intl,
  hasDatesColumn,
  isArchivedExternally,
  hasPricingColumn,
}) {
  let hoursOfEffort = '';
  if (minEffort || maxEffort) {
    hoursOfEffort = intl.formatMessage(messages['prospectus.course.weekly.effort_range'], {
      effortRange: formatNumberRange(minEffort, maxEffort, intl),
    });
  }

  const getStartDate = () => {
    if (isArchivedExternally) {
      return <div className="h4 mb-0">{intl.formatMessage(messages['prospectus.course.snapshot.not-available'])}</div>;
    }

    if (start) {
      return (
        <div className="h4 mb-0">
          <FormattedMessage
            id="prospectus.course.snapshot.start.value"
            defaultMessage="Starts {start}"
            description="This describes the start date of a course."
            values={{
              start: getI18nDateString(start, intl, 'UTC'),
            }}
          />
        </div>
      );
    }

    return null;
  };

  const getEndDate = () => {
    if (isArchivedExternally) {
      return <div className="small">{intl.formatMessage(messages['prospectus.course.snapshot.archived'])}</div>;
    }

    if (courseType === PRODUCT_MAP_2U.EXECUTIVE_EDUCATION.DISCOVERY_KEY && enrollmentEnd) {
      return (
        <div className="small">
          <FormattedMessage
            id="prospectus.course.snapshot.registration-deadline.value"
            defaultMessage="Enroll by {registrationDeadlineDate}"
            description="This describes the enrollment deadline date of a course."
            values={{
              registrationDeadlineDate: getI18nDateString(enrollmentEnd, intl, 'UTC'),
            }}
          />
        </div>
      );
    }

    if (end) {
      return (
        <div className="small">
          <FormattedMessage
            id="prospectus.course.snapshot.end-date.value"
            defaultMessage="Ends {endDate}"
            description="This describes the end date of a course."
            values={{
              endDate: getI18nDateString(end, intl, 'UTC'),
            }}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <WebviewContext.Consumer>
      {webview => {
        const showPricingLanguage = !webview && (coursePrice || paymentOption === FREE);
        const showEnrollDates = (isArchivedExternally || start !== null) && hasDatesColumn !== false;

        // Factoring in the hasPricingColumn to show/hide the pricing tab
        const showPricingColumn = showPricingLanguage && hasPricingColumn;

        let columnClass = 'col-md-3'; // TODO check how this displays when showEnrollDates true
        if ((showPricingLanguage && !showEnrollDates) || (!showPricingLanguage && showEnrollDates)) {
          columnClass = 'col-md-4';
        }

        if (!hasPricingColumn || (!showPricingLanguage && !showEnrollDates)) {
          columnClass = 'col-md-6';
        }

        // Some of video clips didn't update its seat type, it will check if the title contains the keyword "Video Clip"
        // This condition check should be removed when the course owner updated all video clips courses
        const pattern = 'Video Clip: ';
        if (activeCourseRun?.seats[0]?.type === 'video-clip' || new RegExp(pattern, 'gm').test(title)) {
          return null;
        }
        return (
          <div className="course-snapshot-background">
            <Container size="lg" className="d-flex">
              <div
                className={classNames(
                  'course-snapshot-container',
                  { 'wide-container': showPricingLanguage && showEnrollDates },
                  { 'column-container': !hasPricingColumn },
                )}
              >
                <div
                  className={`course-snapshot-content py-2 text-primary-500${
                    !hasPricingColumn ? ' course-pricing-none' : ''
                  }`}
                >
                  <div className="row pl-1 pl-sm-0 my-1">
                    {weeksToComplete > 0 && (
                      <div className={classNames('d-flex align-items-start col-12 pb-4 pb-md-0', columnClass)}>
                        <WeeksToComplete hoursOfEffort={hoursOfEffort} weeksToComplete={weeksToComplete} />
                      </div>
                    )}
                    {showEnrollDates && (
                      <div className={classNames('d-flex align-items-start col-12 pb-4 pb-md-0', columnClass)}>
                        <Icon src={isArchivedExternally ? EventBusy : Calendar} />
                        <div className="ml-3">
                          {getStartDate()}
                          {getEndDate()}
                        </div>
                      </div>
                    )}
                    <div className={classNames('d-flex align-items-start col-12', columnClass)}>
                      <Icon src={Person} />
                      <div className="ml-3">
                        <div className="h4 mb-0">
                          {pacingType === 'self_paced' ? (
                            <FormattedMessage
                              id="prospectus.course.snapshot.pacing.self-paced"
                              defaultMessage="Self-paced"
                              description="Heading describing the pacing type of a self-paced course"
                            />
                          ) : (
                            <FormattedMessage
                              id="prospectus.course.snapshot.pacing.instructor-paced"
                              defaultMessage="Instructor-paced"
                              description="Heading describing the pacing type of an instructor-paced course"
                            />
                          )}
                        </div>
                        <div className="small">
                          {pacingType === 'self_paced' ? (
                            <FormattedMessage
                              id="prospectus.course.snapshot.pacing.self-paced.description"
                              defaultMessage="Progress at your own speed"
                              description="Description of what 'self-paced' means"
                            />
                          ) : (
                            <FormattedMessage
                              id="prospectus.course.snapshot.pacing.instructor-paced.description"
                              defaultMessage="Instructor-led on a course schedule"
                              description="Description of what 'instructor-paced' means"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {showPricingColumn && !isArchived(activeCourseRun) && activeCourseRun.isEnrollable && (
                      <div
                        className={classNames(
                          'd-flex align-items-start col-12 pt-4 pt-md-0',
                          { 'col-md-4': showPricingLanguage && !showEnrollDates },
                          { 'col-md-3': showPricingLanguage && showEnrollDates },
                        )}
                      >
                        <Icon src={MoneyOutline} />
                        <div className="ml-3">
                          {paymentOption === FREE && <FreeLanguage />}
                          {paymentOption === UPGRADABLE && <UpgradableLanguage />}
                          {paymentOption === PAID && (
                            <PaidLanguage coursePrice={coursePrice} userCurrency={userCurrency} />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="snapshot-filler d-none d-md-block" aria-hidden="true" />
            </Container>
          </div>
        );
      }}
    </WebviewContext.Consumer>
  );
}

const mapStateToProps = store => ({
  userCurrency: store.userCurrency || { ...defaultUserCurrency },
  activeCourseRun: store.activeCourseRun,
});

BaseCourseSnapshot.propTypes = {
  courseType: PropTypes.string.isRequired,
  paymentOption: PropTypes.string.isRequired,
  coursePrice: PropTypes.string,
  title: PropTypes.string,
  weeksToComplete: PropTypes.number,
  activeCourseRun: PropTypes.shape({
    minEffort: PropTypes.number,
    maxEffort: PropTypes.number,
    pacingType: PropTypes.string.isRequired,
    start: PropTypes.string,
    hidden: PropTypes.bool,
    isEnrollable: PropTypes.bool,
    end: PropTypes.string,
    enrollmentEnd: PropTypes.string,
    seats: PropTypes.instanceOf(Array),
  }).isRequired,
  userCurrency: currencyInfoShape,
  intl: intlShape.isRequired,
  hasDatesColumn: PropTypes.bool,
  isArchivedExternally: PropTypes.bool,
  hasPricingColumn: PropTypes.bool,
};

BaseCourseSnapshot.defaultProps = {
  coursePrice: null,
  weeksToComplete: 0,
  userCurrency: {},
  hasDatesColumn: false,
  isArchivedExternally: false,
  hasPricingColumn: true,
  title: '',
};
const ConnectedCourseSnapshot = connect(mapStateToProps)(BaseCourseSnapshot);

export default injectIntl(ConnectedCourseSnapshot);
